import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { FilterMatchMode, FilterOperator } from "primereact/api";
import dayjs from "dayjs";
import { isDeepLinkPath } from '../../utils/qcDeepLink';

export const SharedStateContext = createContext();

const QC_FILTER_STORAGE_KEY = "tabsons_qc_grid_filters";

const parseStoredDate = (value, fallback = new Date()) => {
    if (!value) {
        return fallback;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? fallback : parsed;
};

const getStoredQcFilters = () => {
    try {
        const storedValue = localStorage.getItem(QC_FILTER_STORAGE_KEY);
        return storedValue ? JSON.parse(storedValue) : {};
    } catch (error) {
        console.error("Error reading QC filter state:", error);
        return {};
    }
};

const readStoredUserId = () => {
    try {
        return JSON.parse(localStorage.getItem('auth'))?.userID ?? null;
    } catch (error) {
        return null;
    }
};

export const SharedStateProvider = ({ children }) => {
    const count = useRef(0);
    const storedQcFilters = useRef(getStoredQcFilters());
    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [ProgramType, SetProgramType] = useState(storedQcFilters.current.ProgramType || "");
    // The Content Type filter: null (All), "news", "commercial" or "promo".
    // Defaults to All, which is what the grid did before the filter existed --
    // it returns news, commercial and promo rows together.
    const [contentType, setContentType] = useState(storedQcFilters.current.contentType ?? null);
    const [programData, setProgramData] = useState([]);
    const [filterProgramData, setFilterProgramData] = useState([]);
    const [grandTotalProgramData, setGrandTotalProgramData] = useState(0);
    const [AllGeography, setAllGeography] = useState([]);
    const [AllStoryGenre, setAllStoryGenre] = useState([]);
    const [AllStoryFormat, setAllStoryFormat] = useState([]);
    const [channels, setChannels] = useState([]);
    const [scrollHeight, setScrollHeight] = useState("57vh"); // Default value
    const [sortField, setSortField] = useState(null); // State for sort field
    const [sortOrder, setSortOrder] = useState(null); // State for sort order
    const [darkMode, setDarkMode] = useState(false);

    const [RowSelectionItem, setRowSelectionItem] = useState(null);
    const [isMode, setisMode] = useState(null);

    const [selectedChannel, setSelectedChannel] = useState(storedQcFilters.current.selectedChannel || []);
    const [selectedSource, setselectedSource] = useState(storedQcFilters.current.selectedSource || []);
    const [videoLinks, setvideoLinks] = useState(null);
    const [timelineTextData, setTimelineTextData] = useState([]);
    const [keyFramesData, setKeyFramesData] = useState(null);
    const [date, setDate] = useState(parseStoredDate(storedQcFilters.current.date));
    const [time, setTime] = useState(parseStoredDate(storedQcFilters.current.time));
    const [endtime, setEndTime] = useState(parseStoredDate(storedQcFilters.current.endtime));

    const [first, setFirst] = useState(1); // Start with 1 to align with the API
    const [rows, setRows] = useState(25);

    const [form, setForm] = useState({
        search_clusterid: null,
        search_channelid: null,
        search_loggerid: null,
        date_type: "daily",
        search_currentdate: dayjs().subtract(1, 'day'), // Change to dayjs
        search_field: null,
        search_year: null,
        search_week: null,
    });

    const [currentReInferencing, setCurrentReInferencing] = useState([]);
    const updatedRef = useRef([]);

    const [currentReProcessing, setCurrentReProcessing] = useState([]);
    const updatedReProcessingRef = useRef([]);

    const [currentQCComplete, setCurrentQCComplete] = useState([]);
    const updatedQCCompleteRef = useRef([]);
    const [qcCompleteBatch, setQcCompleteBatch] = useState([]); // [{ id, channelName }] shown in the bulk QC progress panel

    // Which user `channels` was loaded for. This provider mounts above the router, so on a
    // fresh visit it mounts on the login page with no user yet; the grid calls
    // ensureChannels after login, and it refetches when a different user logs in.
    const channelsUserIdRef = useRef(null);

    useEffect(() => {
        if (count.current === 0) {
            initFilters();
            ensureChannels(readStoredUserId());
            fetchAllGeography();
            fetchAllStoryGenre();
            fetchAllStoryFormat();
            count.current += 1;
        }
    }, []);

    useEffect(() => {
        // A deep-linked QC form (/qcform/:substoryId) writes the window around its own
        // substory into this context so its fetches have a channel and a day. Those
        // values are read off the row, not chosen by the operator, so they must not
        // replace the grid filters waiting in the operator's other tab.
        if (isDeepLinkPath(window.location.pathname)) {
            return;
        }
        try {
            localStorage.setItem(QC_FILTER_STORAGE_KEY, JSON.stringify({
                selectedChannel,
                selectedSource,
                date: date ? date.toISOString() : null,
                time: time ? time.toISOString() : null,
                endtime: endtime ? endtime.toISOString() : null,
                ProgramType,
                contentType,
            }));
        } catch (error) {
            console.error("Error saving QC filter state:", error);
        }
    }, [selectedChannel, selectedSource, date, time, endtime, ProgramType, contentType]);

    useEffect(() => {
        // Function to calculate available height and set scrollHeight
        const calculateScrollHeight = () => {
            const availableHeight = window.innerHeight;
            const headerHeight = 190; // Adjust based on the size of your headers, etc.
            const calculatedHeight = availableHeight - headerHeight;
            // Set scrollHeight as a percentage of available height
            setScrollHeight(`${calculatedHeight}px`);
        };

        // Calculate on mount
        calculateScrollHeight();

        // Update on window resize
        window.addEventListener("resize", calculateScrollHeight);

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener("resize", calculateScrollHeight);
        };
    }, []);

    const useFetchMasterData = (endpoint, mapFn) => {
        const [data, setData] = useState([]);

        useEffect(() => {
            const fetchData = async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_BASE_URL}${endpoint}`, {
                        method: "GET",
                        headers: {
                            accept: "application/json",
                        },
                    });

                    if (!response.ok) {
                        setData([]);
                        throw new Error("Network response was not ok");
                    }

                    const json = await response.json();
                    const formatted = mapFn(json);
                    setData(formatted);
                } catch (error) {
                    console.error(`Error fetching ${endpoint}:`, error);
                    setData([]);
                }
            };

            fetchData();
        }, [endpoint]);

        return data;
    };

    const AllLanguage = useFetchMasterData(
        "/get_all_languages/",
        (data) => data.map((item) => ({ label: item.language_name, value: item.language_name?.toLowerCase() })).sort((a, b) => a.label.localeCompare(b.label)) // sort alphabetically by label
    );
    const commercialBrandCategories = useFetchMasterData(
        "/api/commercial-brand-categories/?limit=1000&offset=0",
        (data) => data.map((item) => ({ label: item.brand_category, value: item.brand_category?.toLowerCase() })).sort((a, b) => a.label.localeCompare(b.label)) // sort alphabetically by label
    );
    const commercialBrandSectors = useFetchMasterData(
        "/api/commercial-brand-sectors/?limit=1000&offset=0",
        (data) => data.map((item) => ({ label: item.brand_sector, value: item.brand_sector?.toLowerCase() })).sort((a, b) => a.label.localeCompare(b.label)) // sort alphabetically by label
    );
    const commercialAdvertisers = useFetchMasterData(
        "/api/commercial-advertisers/?limit=1000&offset=0",
        (data) => data.map((item) => ({ label: item.advertiser, value: item.advertiser?.toLowerCase() })).sort((a, b) => a.label.localeCompare(b.label)) // sort alphabetically by label
    );
    const commercialAdvertiserGroups = useFetchMasterData(
        "/api/commercial-advertiser-groups/?limit=1000&offset=0",
        (data) => data.map((item) => ({ label: item.advertiser_group, value: item.advertiser_group?.toLowerCase() })).sort((a, b) => a.label.localeCompare(b.label)) // sort alphabetically by label
    );
    const promoAdvertisers = useFetchMasterData(
        "/api/promo-advertisers/?limit=1000&offset=0",
        (data) => data.map((item) => ({ label: item.advertiser, value: item.advertiser?.toLowerCase() })).sort((a, b) => a.label.localeCompare(b.label)) // sort alphabetically by label
    );
    const promoAdvertiserGroups = useFetchMasterData(
        "/api/promo-advertiser-groups/?limit=1000&skip=0",
        (data) => data.map((item) => ({ label: item.advertiser_group, value: item.advertiser_group?.toLowerCase() })).sort((a, b) => a.label.localeCompare(b.label)) // sort alphabetically by label
    );
    const promoBrandSector = useFetchMasterData(
        "/api/promo-brand-sectors/?limit=1000&offset=0",
        (data) => data.map(item => ({
            label: item.brand_sector,
            value: item.brand_sector?.toLowerCase(),
        })).sort((a, b) => a.label.localeCompare(b.label)) // sort alphabetically by label
    );
    const promoBrandCategories = useFetchMasterData(
        "/api/promo-brand-categories/?limit=1000&offset=0",
        (data) => data.map(item => ({
            label: item.brand_category,
            value: item.brand_category?.toLowerCase(),
        })).sort((a, b) => a.label.localeCompare(b.label)) // sort alphabetically by label
    );
    const networks = useFetchMasterData(
        "/get_all_network_master_types/?skip=0&limit=1000",
        (data) => data.map(item => ({
            label: item.network_name,
            value: item.network_name?.toLowerCase(),
        })).sort((a, b) => a.label.localeCompare(b.label)) // sort alphabetically by label
    );

    const fetchAllStoryFormat = async () => {
        try {
            await fetch(
                `${process.env.REACT_APP_BASE_URL}/get_all_storyformat_master_types/`,
                {
                    method: "GET",
                    headers: {
                        accept: "application/json",
                    },
                }
            )
                .then((response) => {
                    if (!response.ok) {
                        setAllStoryFormat([]);
                        // toast.current.show({
                        //     severity: "error",
                        //     summary: "Error",
                        //     detail: "Network response was not ok",
                        // });
                        throw new Error("Network response was not ok");
                    }
                    return response.json();
                })
                .then((data) => {
                    // Filter channels that are active and map them to the required format
                    const StoryFormat = data.map((item) => ({
                        label: item.storyformat_name, // Use channel_name for label
                        value: item.storyformat_name.toLowerCase(), // Use channel_code for value
                    })).sort((a, b) => a.label.localeCompare(b.label)) // sort alphabetically by label
                    setAllStoryFormat(StoryFormat);
                })
                .catch((error) => {
                    // Handle errors here
                    console.error("Error:", error);
                    // toast.current.show({
                    //     severity: "error",
                    //     summary: "Error",
                    //     detail: "Failed to fFetch Story Format",
                    // });
                });
        } catch (error) {
            // ErrorLogger('GET /get_all_storyformat_master_types API/', error, error.message);
            // toast.current.show({
            //     severity: "error",
            //     summary: "Error",
            //     detail: "An error occurred",
            // });
        }
    };
    const fetchAllChannels = async (userId) => {
        if (userId == null) {
            return;
        }
        channelsUserIdRef.current = userId;
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL
                // }/get_all_channels/?skip=${0}&limit=${10000}`,
                }/get_all_channels_ByUserId/${userId}?skip=${0}&limit=${1000}`,
                {
                    method: "GET",
                    headers: {
                        accept: "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const data = await response.json();
            // A later login started its own fetch; this list belongs to someone else.
            if (channelsUserIdRef.current !== userId) {
                return;
            }
            // Filter channels that are active and map them to the required format
            const activeChannels = data
                .filter((channel) => channel.active) // Filter only active channels
                .map((channel) => ({
                    label: channel.channel_name, // Use channel_name for label
                    value: channel.channel_code, // Use channel_code for value
                    network: channel.network, // Use channel_code for value
                    cluster: channel.cluster ?? "", // Keep cluster locally for fast dropdown filtering
                }))
                .sort((a, b) => a.label.localeCompare(b.label)) // sort alphabetically by label;

            // Set the filtered active channels
            setChannels(activeChannels);
        } catch (error) {
            console.error("Error:", error);
            if (channelsUserIdRef.current === userId) {
                setChannels([]);
                // Let the next ensureChannels call retry instead of staying empty.
                channelsUserIdRef.current = null;
            }
        }
    };

    const ensureChannels = useCallback((userId) => {
        if (userId == null || channelsUserIdRef.current === userId) {
            return;
        }
        // Drop the previous user's list so nothing auto-fetches with it.
        setChannels([]);
        fetchAllChannels(userId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const fetchAllGeography = async () => {
        try {
            await fetch(
                `${process.env.REACT_APP_BASE_URL}/get_all_geography_masters/`,
                {
                    method: "GET",
                    headers: {
                        accept: "application/json",
                    },
                }
            )
                .then((response) => {
                    if (!response.ok) {
                        setAllGeography([]);
                        // toast.current.show({
                        //     severity: "error",
                        //     summary: "Error",
                        //     detail: "Network response was not ok",
                        // });
                        throw new Error("Network response was not ok");
                    }
                    return response.json();
                })
                .then((data) => {
                    // Filter channels that are active and map them to the required format
                    const Geography = data.map((item) => ({
                        label: item.geography_name, // Use channel_name for label
                        value: item.geography_name.toLowerCase(), // Use channel_code for value
                    }))
                        .sort((a, b) => a.label.localeCompare(b.label)) // sort alphabetically by label;
                    setAllGeography(Geography);
                })
                .catch((error) => {
                    // Handle errors here
                    console.error("Error:", error);
                    // toast.current.show({
                    //     severity: "error",
                    //     summary: "Error",
                    //     detail: "Failed to Fetch Geographies",
                    // });
                });
        } catch (error) {
            // toast.current.show({
            //     severity: "error",
            //     summary: "Error",
            //     detail: "An error occurred",
            // });
        }
    };
    const fetchAllStoryGenre = async () => {
        try {
            await fetch(
                `${process.env.REACT_APP_BASE_URL}/get_all_story_master_types/`,
                {
                    method: "GET",
                    headers: {
                        accept: "application/json",
                    },
                }
            )
                .then((response) => {
                    if (!response.ok) {
                        setAllStoryGenre([]);
                        // toast.current.show({
                        //     severity: "error",
                        //     summary: "Error",
                        //     detail: "Network response was not ok",
                        // });
                        throw new Error("Network response was not ok");
                    }
                    return response.json();
                })
                .then((data) => {
                    // Filter storyGenre that are active
                    const storyGenre = data
                        .filter((item) => item.active) // Filter only active genres
                        .map((item) => ({
                            label: item.story_genre_name === 'POLITICAL NEWS' ? 'POLITICAL/GOVT NEWS' : item.story_genre_name, // Transform POLITICAL NEWS to POLITICAL/GOVT NEWS
                            value: item.story_genre_name?.toLowerCase(), // Use story_genre_code for value
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label)) // sort alphabetically by label;
                    setAllStoryGenre(storyGenre);
                })
                .catch((error) => {
                    console.error("Error:", error);
                    // toast.current.show({
                    //     severity: "error",
                    //     summary: "Error",
                    //     detail: "Failed to Fetch Story Genre",
                    // });
                });
        } catch (error) {
            console.log("error", error);
            // ErrorLogger('GET /fetchAllStoryGenre API/', error, error.message);
            // toast.current.show({
            //     severity: "error",
            //     summary: "Error",
            //     detail: "An error occurred",
            // });
        }
    };
    const initFilters = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            qc_flag: { value: null, matchMode: FilterMatchMode.IN },
            metadata_origin: { value: null, matchMode: FilterMatchMode.IN },
            // Not a sub_story column: the API answers it with an EXISTS against
            // sub_story_qc_suggestions (QC_GRID_DERIVED_FILTER_FIELDS).
            rag_grounding: { value: null, matchMode: FilterMatchMode.IN },
            anchor_metadata_origin: { value: null, matchMode: FilterMatchMode.IN },
            verification_source: { value: null, matchMode: FilterMatchMode.IN },
            merge_split_status: { value: null, matchMode: FilterMatchMode.IN },
            OtherDetails: { value: null, matchMode: FilterMatchMode.IN },
            validation_flag: { value: null, matchMode: FilterMatchMode.IN },
            channel_code: { value: null, matchMode: FilterMatchMode.IN },
            source: { value: null, matchMode: FilterMatchMode.IN },
            standardized_story: { value: null, matchMode: FilterMatchMode.IN },
            substory_category: { value: null, matchMode: FilterMatchMode.IN },
            channel_name: { value: null, matchMode: FilterMatchMode.IN },
            channel_language: { value: null, matchMode: FilterMatchMode.IN },
            start_time: { value: null, matchMode: FilterMatchMode.IN },
            end_time: { value: null, matchMode: FilterMatchMode.IN },
            substory: { value: null, matchMode: FilterMatchMode.IN },
            text_content: { value: null, matchMode: FilterMatchMode.IN },
            story_format: { value: null, matchMode: FilterMatchMode.IN },
            program_date: { value: null, matchMode: FilterMatchMode.IN },
            program_start_time: { value: null, matchMode: FilterMatchMode.IN },
            nst_program_start: { value: null, matchMode: FilterMatchMode.IN },
            clip_start_time: { value: null, matchMode: FilterMatchMode.IN },
            nst_clip_start: { value: null, matchMode: FilterMatchMode.IN },
            program_end_time: { value: null, matchMode: FilterMatchMode.IN },
            nst_program_end: { value: null, matchMode: FilterMatchMode.IN },
            clip_end_time: { value: null, matchMode: FilterMatchMode.IN },
            nst_clip_end: { value: null, matchMode: FilterMatchMode.IN },
            duration_hhmmss: { value: null, matchMode: FilterMatchMode.IN },
            duration_seconds: { value: null, matchMode: FilterMatchMode.IN },
            clip_url: { value: null, matchMode: FilterMatchMode.IN },
            story_genre: { value: null, matchMode: FilterMatchMode.IN },
            substory_genre: { value: null, matchMode: FilterMatchMode.IN },
            geography: { value: null, matchMode: FilterMatchMode.IN },
            cluster: { value: null, matchMode: FilterMatchMode.IN },
            logistics: { value: null, matchMode: FilterMatchMode.IN },
            split: { value: null, matchMode: FilterMatchMode.IN },
            live: { value: null, matchMode: FilterMatchMode.IN },
            exclusive: { value: null, matchMode: FilterMatchMode.IN },
            program_name: { value: null, matchMode: FilterMatchMode.IN },
            program_type: { value: null, matchMode: FilterMatchMode.IN },
            week_num: { value: null, matchMode: FilterMatchMode.IN },
            week_day: { value: null, matchMode: FilterMatchMode.IN },
            promo: { value: null, matchMode: FilterMatchMode.IN },
            commercial: { value: null, matchMode: FilterMatchMode.IN },
            telecast_format: { value: null, matchMode: FilterMatchMode.IN },
            event_conclave: { value: null, matchMode: FilterMatchMode.IN },
            story: { value: null, matchMode: FilterMatchMode.IN },
            personality: { value: null, matchMode: FilterMatchMode.IN },
            guest: { value: null, matchMode: FilterMatchMode.IN },
            anchor: { value: null, matchMode: FilterMatchMode.IN },
            reporter: { value: null, matchMode: FilterMatchMode.IN },
            brand: { value: null, matchMode: FilterMatchMode.IN },
        });
        setGlobalFilterValue("");
    };

    const updateReinferencingStatus = (data) => {
        const { id, reinferencing_status } = data;

        // Update programData
        setProgramData((prevData) =>
            prevData.map((item) =>
                item.id === id ? { ...item, reinferencing_status } : item
            )
        );

        // Update filterProgramData if it exists
        setFilterProgramData((prevData) =>
            prevData.map((item) =>
                item.id === id ? { ...item, reinferencing_status } : item
            )
        );
    };

    return (
        <SharedStateContext.Provider
            value={{
                programData, setProgramData,
                filterProgramData, setFilterProgramData,
                grandTotalProgramData, setGrandTotalProgramData,
                AllGeography,
                AllStoryGenre,
                AllStoryFormat,
                channels,
                ensureChannels,
                filters, setFilters,
                globalFilterValue, setGlobalFilterValue,
                initFilters,
                scrollHeight, setScrollHeight,
                sortField, setSortField,
                sortOrder, setSortOrder,
                selectedChannel, setSelectedChannel,
                selectedSource, setselectedSource,
                date, setDate,
                time, setTime,
                endtime, setEndTime,
                RowSelectionItem, setRowSelectionItem,
                isMode, setisMode,
                ProgramType, SetProgramType,
                contentType, setContentType,
                videoLinks, setvideoLinks,
                keyFramesData, setKeyFramesData,
                timelineTextData, setTimelineTextData,
                form, setForm,
                first, setFirst,
                rows, setRows,
                updateReinferencingStatus,
                commercialBrandCategories,
                commercialBrandSectors,
                commercialAdvertisers,
                commercialAdvertiserGroups,
                AllLanguage,
                promoBrandCategories,
                promoAdvertisers,
                promoAdvertiserGroups,
                promoBrandSector,
                networks,
                darkMode, setDarkMode,
                currentReInferencing, setCurrentReInferencing,
                updatedRef,
                currentReProcessing, setCurrentReProcessing,
                updatedReProcessingRef,
                currentQCComplete, setCurrentQCComplete,
                updatedQCCompleteRef,
                qcCompleteBatch, setQcCompleteBatch
            }}>
            {children}
        </SharedStateContext.Provider>
    );
};
